import { useEffect, useState } from 'react';

import useChemical from 'hooks/useChemical';
import type { EnrichedChemical } from 'hooks/useChemical/types';
import type { MixRecipeProduct, MixRecipeProductSignalWord } from 'hooks/useMixRecipe/types';

import type {
    MixRecipe,
    UseEnrichRecipeProductsParams,
    UseEnrichRecipeProductsReturn
} from './types';

const SIGNAL_WORD_MAP: Record<string, MixRecipeProductSignalWord> = {
    caution: 'caution',
    danger: 'danger',
    warning: 'warning'
};

const UUID_SHAPE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const normalizeSignalWord = (raw: string | null): MixRecipeProductSignalWord | undefined => {
    if (!raw) {
        return undefined;
    }

    return SIGNAL_WORD_MAP[raw.toLowerCase()];
};

const mergeEnrichment = (
    product: MixRecipeProduct,
    enriched: EnrichedChemical | null
): MixRecipeProduct => {
    if (!enriched) {
        return product;
    }

    const labelRateRange =
        enriched.genericMinRate !== null && enriched.genericMaxRate !== null
            ? { maximum: enriched.genericMaxRate, minimum: enriched.genericMinRate }
            : product.labelRateRange;

    const ingredients = enriched.ingredients.map(ingredient => ({
        acidEquivalentPercentage: ingredient.acidEquivalentPercentage ?? null,
        casNumber: ingredient.casNumber,
        name: ingredient.name,
        percentage: ingredient.percentage
    }));

    const classifications = enriched.classifications.map(classification => ({
        name: classification.name,
        primary: classification.primary
    }));

    return {
        ...product,
        classifications,
        densityLbPerGal: enriched.density ?? null,
        formulation: enriched.formulationName ?? product.formulation,
        formulationCode: enriched.formulationCode ?? product.formulationCode ?? null,
        ingredients,
        isRestrictedUse: enriched.isRestrictedUse ?? product.isRestrictedUse,
        labelRateRange,
        manufacturer: enriched.manufacturer ?? product.manufacturer,
        mixOrderGroup: enriched.mixOrderGroup ?? product.mixOrderGroup ?? null,
        modeOfActionCode: enriched.modeOfActions[0] ?? product.modeOfActionCode,
        physicalState: enriched.physicalState ?? product.physicalState ?? null,
        signalWord: normalizeSignalWord(enriched.signalWord) ?? product.signalWord
    };
};

const useEnrichRecipeProducts = ({
    mixRecipe
}: UseEnrichRecipeProductsParams): UseEnrichRecipeProductsReturn => {
    // Initialize as null (not the un-enriched mixRecipe prop). If we initialized
    // with the prop, the advisory hooks would fire on first render with products
    // that have empty ingredients and null formulationCode — backends 400 those
    // requests and the UI gets stuck in the error toast even though the second
    // (post-enrichment) call would have succeeded.
    const [enrichedMixRecipe, setEnrichedMixRecipe] = useState<MixRecipe | null>(null);
    const [isEnriching, setIsEnriching] = useState(false);
    const { fetchChemical } = useChemical();

    useEffect(() => {
        if (!mixRecipe) {
            setEnrichedMixRecipe(null);

            return;
        }

        if (mixRecipe.products.length === 0) {
            setEnrichedMixRecipe(mixRecipe);

            return;
        }

        let cancelled = false;

        (async () => {
            try {
                setIsEnriching(true);

                const enrichedResults = await Promise.all(
                    mixRecipe.products.map(product =>
                        UUID_SHAPE.test(product.productId)
                            ? fetchChemical({ id: product.productId })
                            : Promise.resolve(null)
                    )
                );

                if (cancelled) {
                    return;
                }

                const mergedProducts = mixRecipe.products.map((product, index) =>
                    mergeEnrichment(product, enrichedResults[index])
                );

                setEnrichedMixRecipe({ ...mixRecipe, products: mergedProducts });
            } finally {
                if (!cancelled) {
                    setIsEnriching(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
        // Re-enrich when the recipe identity changes OR when the set of
        // product IDs changes (server-side mutation on the same recipe id).
        // Depending solely on mixRecipeId would skip re-enrichment for a
        // product-swap that kept the id, leaving new products un-enriched
        // (missing formulationCode / mixOrderGroup / density) and silently
        // mis-classifying them in mix-order and under-counting their volume.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mixRecipe?.mixRecipeId, mixRecipe?.products.map(product => product.productId).join(',')]);

    return { enrichedMixRecipe, isEnriching };
};

export default useEnrichRecipeProducts;
